import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';

import DateRangePicker, {
  DateRangePickerDateSelectFunction,
} from '~components/Filters/DateRangePicker';
import { useStores } from '~store';
import { LiveMapStorageKeys } from '~store/LiveMapStoreNew';
import { persistDateRange } from '~utils/dateRangePersistence';

const LiveMapDateFilter = observer(() => {
  const { liveMapStoreNew } = useStores();

  const initialStartDate = liveMapStoreNew.filters.startDate
    ? dayjs(liveMapStoreNew.filters.startDate)
    : null;
  const initialEndDate = liveMapStoreNew.filters.endDate
    ? dayjs(liveMapStoreNew.filters.endDate)
    : null;

  const [dateRange, setDateRange] = useState({
    start: initialStartDate,
    end: initialEndDate,
  });

  // The picker's local state initializes once on mount, so a store-side clear
  // (e.g. clearAllFiltersExceptSearch from the no-results hint) would otherwise
  // keep showing the cleared dates.
  useEffect(() => {
    setDateRange({
      start: liveMapStoreNew.filters.startDate
        ? dayjs(liveMapStoreNew.filters.startDate)
        : null,
      end: liveMapStoreNew.filters.endDate
        ? dayjs(liveMapStoreNew.filters.endDate)
        : null,
    });
  }, [liveMapStoreNew.filters.startDate, liveMapStoreNew.filters.endDate]);

  const handleDateFilterChange = useCallback<DateRangePickerDateSelectFunction>(
    (startDate, endDate) => {
      const start = startDate;
      const end = endDate ?? startDate;

      setDateRange({ start, end });
    },
    [],
  );

  const handleDateFilterClose = useCallback(() => {
    const startDate = dateRange.start?.format('YYYY-MM-DD');
    const endDate = dateRange.end?.format('YYYY-MM-DD');

    const startDateDidChange = liveMapStoreNew.filters.startDate !== startDate;
    const endDateDidChange = liveMapStoreNew.filters.endDate !== endDate;

    if (startDateDidChange || endDateDidChange) {
      liveMapStoreNew.setFilters({ startDate, endDate }, true);

      // An "All dates" clear persists as explicit nulls: the user must not be
      // re-defaulted into today's range on return (TRE-15856).
      persistDateRange(LiveMapStorageKeys.APPLIED_DATE_FILTERS, {
        endDate: endDate ?? null,
        startDate: startDate ?? null,
      });
    }
  }, [
    dateRange.start,
    dateRange.end,
    liveMapStoreNew.filters.startDate,
    liveMapStoreNew.filters.endDate,
  ]);

  return (
    <DateRangePicker
      allowAllDates
      endDate={dateRange.end}
      mode="icon"
      onAnchorClose={handleDateFilterClose}
      onDateSelect={handleDateFilterChange}
      showShortcuts
      startDate={dateRange.start}
      useTodayFormat
      variant="single_date"
    />
  );
});

export default LiveMapDateFilter;
